// hooks/useThree.ts
import { type RefObject, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";

// Helper function to get current theme
const getCurrentTheme = (): "light" | "dark" => {
  if (typeof window === "undefined") return "light";
  const rootElement = document.documentElement;
  return rootElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
};

// Helper function to create background texture based on theme
const createBackgroundTexture = (
  theme: "light" | "dark"
): THREE.CanvasTexture => {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const context = canvas.getContext("2d");

  if (context) {
    // Create vertical gradient
    const gradient = context.createLinearGradient(0, 0, 0, canvas.height);

    if (theme === "dark") {
      // Dark mode gradient - from dark blue-gray to almost black
      gradient.addColorStop(0, "#1a1a2e"); // Dark blue-gray at top
      gradient.addColorStop(0.6, "#16213e"); // Darker blue in middle
      gradient.addColorStop(1, "#0f1419"); // Very dark blue-black at bottom
    } else {
      // Light mode gradient - from sky blue to white
      gradient.addColorStop(0, "#87CEEB"); // Light Sky Blue at top
      gradient.addColorStop(0.6, "#B8D4F0"); // Lighter blue in middle
      gradient.addColorStop(1, "#F0F8FF"); // Alice Blue (almost white) at bottom
    }

    context.fillStyle = gradient;
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  return new THREE.CanvasTexture(canvas);
};

export const useThree = (
  mountRef: RefObject<HTMLDivElement | null>,
  _isEditMode: boolean
) => {
  const sceneRef = useRef<THREE.Scene>(new THREE.Scene());
  const cameraRef = useRef<THREE.PerspectiveCamera>(undefined);
  const rendererRef = useRef<THREE.WebGLRenderer>(undefined);
  const controlsRef = useRef<OrbitControls>(undefined);
  const animationFrameRef = useRef<number>(undefined);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [currentTheme, setCurrentTheme] = useState<"light" | "dark">(() =>
    getCurrentTheme()
  );

  // Internal tick used to retry initialization once the container has a non-zero size
  const [containerReadyTick, setContainerReadyTick] = useState(0);

  // Listen for theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      const newTheme = getCurrentTheme();
      setCurrentTheme(newTheme);
    };

    // Watch for changes to the data-theme attribute
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "data-theme"
        ) {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Update scene background when theme changes
  useEffect(() => {
    if (
      !sceneRef.current ||
      !rendererRef.current ||
      !cameraRef.current ||
      !isSceneReady
    )
      return;

    const localScene = sceneRef.current;

    // Check if background is already overridden by color palette
    // If so, don't change it to avoid interfering with custom palettes
    if (
      localScene.background instanceof THREE.CanvasTexture &&
      localScene.background.image?.width === 512 &&
      localScene.background.image?.height === 512
    ) {
      // This might be a color palette background, don't override it
      return;
    }

    // Dispose old background texture if it exists
    if (localScene.background instanceof THREE.Texture) {
      localScene.background.dispose();
    }

    // Create new background texture for current theme
    const backgroundTexture = createBackgroundTexture(currentTheme);
    localScene.background = backgroundTexture;

    // Update fog color to match theme
    let fogColor: THREE.Color;
    if (currentTheme === "dark") {
      fogColor = new THREE.Color(0x0f1419); // Very dark blue-black
    } else {
      fogColor = new THREE.Color(0xe6f3ff); // Very light blue-white
    }

    localScene.fog = new THREE.Fog(fogColor, 15, 120);
    rendererRef.current.setClearColor(fogColor);

    // Update existing clouds to match new theme
    localScene.children.forEach((child) => {
      if (child.userData.isCloud && child instanceof THREE.Mesh) {
        const cloudMesh = child as THREE.Mesh;
        if (cloudMesh.material instanceof THREE.MeshBasicMaterial) {
          cloudMesh.material.color.setHex(
            currentTheme === "dark" ? 0x2a2a3e : 0xffffff
          );
          cloudMesh.material.opacity = currentTheme === "dark" ? 0.08 : 0.15;
          cloudMesh.material.needsUpdate = true;
        }
      }
    });

    // Ensure the canvas is still properly attached to the mount element
    const mountElement = mountRef.current;
    if (
      mountElement &&
      rendererRef.current?.domElement &&
      !mountElement.contains(rendererRef.current.domElement)
    ) {
      mountElement.appendChild(rendererRef.current.domElement);
    }

    // Update renderer size to ensure proper display after theme change
    if (mountElement && rendererRef.current && cameraRef.current) {
      const width = mountElement.clientWidth;
      const height = mountElement.clientHeight;
      rendererRef.current.setSize(width, height);
      cameraRef.current.aspect = width / height;
      cameraRef.current.updateProjectionMatrix();
    }

    // Force a render to show the changes immediately
    rendererRef.current.render(localScene, cameraRef.current);

    // Use isSceneReady and mountRef.current to satisfy lint
    void isSceneReady;
    void mountRef.current;
  }, [currentTheme, isSceneReady, mountRef]);

  const currentThemeRef = useRef(currentTheme);
  useEffect(() => {
    currentThemeRef.current = currentTheme;
  }, [currentTheme]);

  useEffect(() => {
    const mountElement = mountRef.current;
    if (!mountElement) {
      setIsSceneReady(false);
      return;
    }

    const localScene = sceneRef.current; // sceneRef.current is stable

    // Check if we have a valid existing setup
    const hasValidSetup =
      mountElement.dataset.threeInitialized === "true" &&
      mountElement.dataset.sceneWasReady === "true" &&
      rendererRef.current &&
      cameraRef.current &&
      controlsRef.current &&
      mountElement.contains(rendererRef.current.domElement);

    if (hasValidSetup) {
      // Update controls based on isEditMode. This is crucial if isEditMode is a dependency.
      if (controlsRef.current) {
        // Panning should be enabled by default, even in edit mode.
        controlsRef.current.enablePan = true;
      }

      // Ensure the scene is marked as ready
      setIsSceneReady(true);
      return;
    }

    // If we had a partial setup, clean it up first
    if (mountElement.dataset.threeInitialized === "true") {
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }

      // Clean up existing renderer and canvas
      if (rendererRef.current) {
        const existingCanvas = mountElement.querySelector("canvas");
        if (existingCanvas) {
          mountElement.removeChild(existingCanvas);
        }
        rendererRef.current.dispose();
        rendererRef.current = undefined;
      }

      // Clean up controls
      if (controlsRef.current) {
        controlsRef.current.dispose();
        controlsRef.current = undefined;
      }

      // Reset camera
      cameraRef.current = undefined;

      // Reset dataset flags
      mountElement.dataset.threeInitialized = "false";
      mountElement.dataset.sceneWasReady = "false";
    }
    // Mark this element as having had its Three.js setup initiated for this effect run.
    mountElement.dataset.threeInitialized = "true";
    // sceneWasReady is set to true at the *end* of successful setup.
    mountElement.dataset.sceneWasReady = "false";

    // Pre-check container size; if invalid, observe and retry, skipping initialization for now
    const preWidth = mountElement.clientWidth || 800;
    const preHeight = mountElement.clientHeight || 600;
    if (preWidth <= 0 || preHeight <= 0) {
      mountElement.dataset.threeInitialized = "false";
      mountElement.dataset.sceneWasReady = "false";
      setIsSceneReady(false);

      // If ResizeObserver is available, wait for the element to have a non-zero size, then retry init
      let resizeObserver: ResizeObserver | null = null;
      let retried = false;
      const tryKickoff = () => {
        if (!mountElement || retried) return;
        const w = mountElement.clientWidth;
        const h = mountElement.clientHeight;
        if (w > 0 && h > 0) {
          retried = true;
          if (resizeObserver) {
            resizeObserver.disconnect();
            resizeObserver = null;
          }
          // Trigger re-run of this effect to initialize with valid size
          setContainerReadyTick((v) => v + 1);
        }
      };

      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(() => {
          tryKickoff();
        });
        resizeObserver.observe(mountElement);
        return () => {
          if (resizeObserver) {
            resizeObserver.disconnect();
          }
        };
      }

      // Fallback: poll if ResizeObserver is unavailable
      const pollId = window.setInterval(() => {
        tryKickoff();
      }, 100);
      return () => {
        window.clearInterval(pollId);
      };
    }

    try {
      setIsSceneReady(false); // Explicitly false until setup completes
      localScene.clear(); // Clear previous objects, lights, etc., from the stable sceneRef

      // Check for any existing canvases and remove them
      const existingCanvases = mountElement.querySelectorAll("canvas");
      existingCanvases.forEach((canvas) => {
        canvas.remove();
      });

      const containerWidth = preWidth;
      const containerHeight = preHeight;

      // Create new instances for this setup
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: false, // Fog works best with alpha: false
        powerPreference: "high-performance",
      });
      renderer.setSize(containerWidth, containerHeight);
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFShadowMap;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.9;

      // Force canvas to be visible and above other elements
      renderer.domElement.style.position = "relative";
      renderer.domElement.style.zIndex = "1";
      renderer.domElement.style.display = "block";
      // Ensure canvas CSS size matches container (important for proper display)
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";

      // Create background texture based on current theme
      const backgroundTexture = createBackgroundTexture(
        currentThemeRef.current
      );
      localScene.background = backgroundTexture;

      // Set fog color based on theme
      let fogColor: THREE.Color;
      if (currentThemeRef.current === "dark") {
        fogColor = new THREE.Color(0x0f1419); // Very dark blue-black
      } else {
        fogColor = new THREE.Color(0xe6f3ff); // Very light blue-white
      }

      localScene.fog = new THREE.Fog(fogColor, 15, 120);
      renderer.setClearColor(fogColor);

      mountElement.appendChild(renderer.domElement);

      const camera = new THREE.PerspectiveCamera(
        50,
        containerWidth / containerHeight,
        0.1,
        1000
      );
      camera.position.set(5, 5, 10);
      camera.lookAt(0, 2, 0);
      localScene.add(camera); // Add camera to the scene

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.8);
      localScene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2);
      directionalLight.position.set(5, 10, 7.5);
      directionalLight.castShadow = true;
      directionalLight.shadow.mapSize.set(2048, 2048);
      directionalLight.shadow.camera.near = 0.5;
      directionalLight.shadow.camera.far = 50;
      localScene.add(directionalLight);
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
      fillLight.position.set(-5, 5, -5);
      localScene.add(fillLight);

      // Add some cloud-like spheres for atmospheric effect
      const cloudGeometry = new THREE.SphereGeometry(8, 16, 8);
      const cloudMaterial = new THREE.MeshBasicMaterial({
        color: currentTheme === "dark" ? 0x2a2a3e : 0xffffff, // Darker clouds for dark mode
        transparent: true,
        opacity: currentTheme === "dark" ? 0.08 : 0.15, // More subtle in dark mode
        fog: false, // Clouds shouldn't be affected by fog
      });

      // Create several clouds at different positions
      const cloudPositions = [
        { x: -30, y: 20, z: -40 },
        { x: 25, y: 15, z: -35 },
        { x: -15, y: 25, z: -50 },
        { x: 40, y: 18, z: -30 },
        { x: -45, y: 22, z: -45 },
        { x: 20, y: 30, z: -60 },
      ];

      cloudPositions.forEach((pos, _index) => {
        const cloud = new THREE.Mesh(cloudGeometry, cloudMaterial.clone());
        cloud.position.set(pos.x, pos.y, pos.z);
        cloud.scale.setScalar(0.8 + Math.random() * 0.4); // Vary cloud size
        cloud.userData.isCloud = true;
        localScene.add(cloud);
      });

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.minDistance = 0; // Allow camera to pass through model
      controls.maxDistance = 60;
      controls.minPolarAngle = Math.PI / 4; // Prevent looking from below
      controls.maxPolarAngle = (Math.PI * 3) / 4; // Prevent looking from directly above
      controls.enableDamping = true;
      controls.dampingFactor = 0.05;
      controls.screenSpacePanning = true; // Use true for more intuitive panning
      controls.target.set(0, 2, 0); // Initial target
      controls.enablePan = true; // Ensure panning is enabled by default
      controls.listenToKeyEvents(window); // For keyboard controls

      // Enhanced mouse controls
      controls.mouseButtons = {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.PAN,
        RIGHT: THREE.MOUSE.PAN,
      };

      // Enhanced touch controls for mobile/trackpad
      controls.touches = {
        ONE: THREE.TOUCH.ROTATE,
        TWO: THREE.TOUCH.DOLLY_PAN,
      };

      // More responsive zoom
      controls.zoomSpeed = 1.2;
      controls.panSpeed = 1.0;
      controls.rotateSpeed = 1.0;

      // Assign to refs *after* full initialization of new instances
      cameraRef.current = camera;
      rendererRef.current = renderer;
      controlsRef.current = controls;

      let _renderCount = 0;
      const animate = () => {
        if (
          !rendererRef.current ||
          !cameraRef.current ||
          !controlsRef.current ||
          !sceneRef.current
        ) {
          animationFrameRef.current = requestAnimationFrame(animate);
          return;
        }
        controlsRef.current.update();
        rendererRef.current.render(sceneRef.current, cameraRef.current);

        _renderCount++;

        animationFrameRef.current = requestAnimationFrame(animate);
      };
      animate();

      const handleResize = () => {
        if (!mountElement || !cameraRef.current || !rendererRef.current) return;
        const newWidth = mountElement.clientWidth;
        const newHeight = mountElement.clientHeight;
        cameraRef.current.aspect = newWidth / newHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(newWidth, newHeight);
      };
      window.addEventListener("resize", handleResize);

      // Also use ResizeObserver for container-specific size changes
      let resizeObserver: ResizeObserver | null = null;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === mountElement) {
              handleResize();
              break;
            }
          }
        });
        resizeObserver.observe(mountElement);
      }

      renderer.render(localScene, camera); // Use newly created camera and renderer

      setIsSceneReady(true);
      mountElement.dataset.sceneWasReady = "true";

      return () => {
        // Cancel animation frame first
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = undefined;
        }

        window.removeEventListener("resize", handleResize);

        // Clean up ResizeObserver
        if (resizeObserver) {
          resizeObserver.disconnect();
        }

        // Properly dispose of controls and renderer
        if (controlsRef.current) {
          controlsRef.current.dispose();
          controlsRef.current = undefined;
        }

        // Dispose of WebGL context properly
        if (rendererRef.current) {
          const gl = rendererRef.current.getContext();
          const loseContextExt = gl?.getExtension?.("WEBGL_lose_context");
          if (loseContextExt) {
            loseContextExt.loseContext();
          }
          rendererRef.current.dispose();
          rendererRef.current = undefined;
        }

        // Clean up scene resources
        if (sceneRef.current) {
          // Dispose of all meshes and their geometries/materials
          sceneRef.current.traverse((child) => {
            if (child instanceof THREE.Mesh) {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach((material) => {
                    material.dispose();
                  });
                } else {
                  child.material.dispose();
                }
              }
            }
          });

          // Clear fog
          if (sceneRef.current.fog) sceneRef.current.fog = null;

          // Dispose of background texture
          if (sceneRef.current.background instanceof THREE.Texture) {
            sceneRef.current.background.dispose();
            sceneRef.current.background = null;
          } else if (sceneRef.current.background instanceof THREE.Color) {
            sceneRef.current.background = null;
          }

          // Clear scene
          sceneRef.current.clear();
        }

        // Remove canvas from DOM
        if (
          mountElement &&
          rendererRef.current?.domElement &&
          mountElement.contains(rendererRef.current.domElement)
        ) {
          mountElement.removeChild(rendererRef.current.domElement);
        }

        // Reset dataset flags for the specific mountElement instance
        mountElement.dataset.threeInitialized = "false";
        mountElement.dataset.sceneWasReady = "false";
        setIsSceneReady(false);

        // Clear camera reference
        cameraRef.current = undefined;
      };
    } catch (error) {
      console.error(
        "useThree: Error setting up scene with theme-adaptive background:",
        error
      );
      mountElement.dataset.threeInitialized = "false";
      mountElement.dataset.sceneWasReady = "false";
      setIsSceneReady(false);
    }
    // Use containerReadyTick to satisfy lint while keeping it as a trigger
    void containerReadyTick;
  }, [mountRef, containerReadyTick, currentTheme]); // Re-initialize when container becomes sized

  return {
    scene: sceneRef.current,
    camera: cameraRef.current,
    renderer: rendererRef.current,
    controls: controlsRef.current,
    isSceneReady,
  };
};
